/**
 * 組織設定/部門ページ (/org-settings/department)
 *
 * サイドバーメニュー「組織設定」配下のタブとして表示される。
 */
import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import PageHeader from "../components/PageHeader";
import { TabBar } from "../components/molecules/TabBar";
import { ActionIcon } from "../components/atoms/Icon";
import {
    ColumnFilterPopup,
    useColumnFilter,
    type SortDirection,
} from "../components/molecules/ColumnFilterPopup";
import { useMenuTitle } from "../shared/menu/useMenuTitle";
import { fetchDepartments, type DepartmentResponse } from "../api/departments";
import {
    CommonTable,
    CommonTableHeaderCell,
    CommonTableEditHeaderCell,
    CommonTableRow,
    CommonTableCell,
    CommonTableEditCell,
} from "../components/table";

const DEPARTMENT_COLUMN_COUNT = 4;
const EMPTY_VALUE_KEY = "__empty__";
const EMPTY_VALUE_LABEL = "(未設定)";

type SortColumn = "code" | "name" | "shortName";

const displayValue = (val: string | null | undefined): string =>
    val === null || val === undefined || val === "" ? "-" : val;

const toKey = (val: string | null | undefined): string =>
    val === null || val === undefined || val === "" ? EMPTY_VALUE_KEY : val;

const labelOrEmpty = (key: string): string =>
    key === EMPTY_VALUE_KEY ? EMPTY_VALUE_LABEL : key;

// バックエンドの schema が確定するまでの暫定アクセサ。
// API がフィールドを返すようになったら、この関数を差し替えるだけで表示・並び替え・絞り込みが動く。
const getShortName = (_d: DepartmentResponse): string | null => null;

const OrgSettingsDepartmentPage = () => {
    const menuTitle = useMenuTitle();
    const navigate = useNavigate();
    const [departments, setDepartments] = useState<DepartmentResponse[]>([]);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        const load = async () => {
            setLoading(true);
            try {
                const res = await fetchDepartments();
                setDepartments(res.items);
            } catch (error) {
                console.error("Failed to fetch departments:", error);
            } finally {
                setLoading(false);
            }
        };
        load();
    }, []);

    // 列ごとの並び替え/絞り込み state
    const codeFilter = useColumnFilter(departments, (d) => toKey(d.code), labelOrEmpty);
    const nameFilter = useColumnFilter(departments, (d) => toKey(d.name), labelOrEmpty);
    const shortNameFilter = useColumnFilter(departments, (d) => toKey(getShortName(d)), labelOrEmpty);

    // 並び替えは「同時に1列のみ」アクティブ
    const [activeSort, setActiveSort] = useState<{
        column: SortColumn;
        direction: "asc" | "desc";
    } | null>(null);

    const sortFor = (column: SortColumn): SortDirection =>
        activeSort?.column === column ? activeSort.direction : null;

    const handleSortChange = useCallback(
        (column: SortColumn) => (dir: SortDirection) => {
            setActiveSort(dir === null ? null : { column, direction: dir });
        },
        [],
    );

    // 表示用にソート/絞り込みを適用
    const displayedDepartments = useMemo(() => {
        let list = departments;

        if (codeFilter.isFiltering) {
            const set = new Set(codeFilter.selected);
            list = list.filter((d) => set.has(toKey(d.code)));
        }
        if (nameFilter.isFiltering) {
            const set = new Set(nameFilter.selected);
            list = list.filter((d) => set.has(toKey(d.name)));
        }
        if (shortNameFilter.isFiltering) {
            const set = new Set(shortNameFilter.selected);
            list = list.filter((d) => set.has(toKey(getShortName(d))));
        }

        if (activeSort) {
            const cmpString = (
                getter: (d: DepartmentResponse) => string | null,
            ) => (a: DepartmentResponse, b: DepartmentResponse): number =>
                (getter(a) ?? "").localeCompare(getter(b) ?? "", "ja");
            const cmpByColumn: Record<SortColumn, (a: DepartmentResponse, b: DepartmentResponse) => number> = {
                code: cmpString((d) => d.code),
                name: cmpString((d) => d.name),
                shortName: cmpString(getShortName),
            };
            const cmp = cmpByColumn[activeSort.column];
            list = [...list].sort((a, b) =>
                activeSort.direction === "asc" ? cmp(a, b) : -cmp(a, b),
            );
        }

        return list;
    }, [
        departments,
        codeFilter.isFiltering,
        codeFilter.selected,
        nameFilter.isFiltering,
        nameFilter.selected,
        shortNameFilter.isFiltering,
        shortNameFilter.selected,
        activeSort,
    ]);

    // ヘッダークリック時、ポップアップは「同時に1つだけ」開く
    const openOnly = useCallback(
        (which: SortColumn, anchor: HTMLButtonElement) => {
            const map = {
                code: codeFilter,
                name: nameFilter,
                shortName: shortNameFilter,
            } as const;
            const target = map[which];
            target.anchorRef.current = anchor;
            const willOpen = !target.open;
            target.setOpen(willOpen);
            if (willOpen) {
                Object.entries(map).forEach(([col, f]) => {
                    if (col !== which) f.setOpen(false);
                });
            }
        },
        [codeFilter, nameFilter, shortNameFilter],
    );

    return (
        <div className="flex flex-1 flex-col overflow-hidden">
            <PageHeader title={menuTitle} />
            <TabBar parentCode="org_settings" />

            {/* アクションボタン */}
            <div className="px-6 pb-4 flex items-center justify-between">
                <div className="flex items-center gap-3">
                    <button
                        type="button"
                        onClick={() => navigate("/org-settings/department/new")}
                        className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-brand-500 text-white text-base font-medium hover:bg-brand-600 transition-colors"
                    >
                        <ActionIcon name="add" className="w-4 h-4" />
                        新規追加
                    </button>
                    <button
                        type="button"
                        disabled
                        className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-gray-300 text-gray-500 text-base font-medium cursor-not-allowed"
                    >
                        <ActionIcon name="edit" className="w-4 h-4" />
                        一括編集
                    </button>
                    <button
                        type="button"
                        disabled
                        className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-gray-300 text-gray-500 text-base font-medium cursor-not-allowed"
                    >
                        <ActionIcon name="upload" className="w-4 h-4" />
                        アップロード
                    </button>
                    <button
                        type="button"
                        disabled
                        className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-gray-300 text-gray-500 text-base font-medium cursor-not-allowed"
                    >
                        <ActionIcon name="download" className="w-4 h-4" />
                        ダウンロード
                    </button>
                </div>
            </div>

            {/* 部門テーブル */}
            <div className="flex-1 min-h-0 px-6 pb-6">
                <CommonTable>
                    <thead className="sticky top-0 z-20">
                        <tr>
                            <CommonTableEditHeaderCell />
                            <CommonTableHeaderCell
                                sortable
                                sortActive={codeFilter.open}
                                sortDirection={sortFor("code")}
                                filterCount={codeFilter.filterCount}
                                onSort={(anchor) => openOnly("code", anchor)}
                                className="min-w-[200px]"
                            >
                                部門コード
                            </CommonTableHeaderCell>
                            <CommonTableHeaderCell
                                sortable
                                sortActive={nameFilter.open}
                                sortDirection={sortFor("name")}
                                filterCount={nameFilter.filterCount}
                                onSort={(anchor) => openOnly("name", anchor)}
                                className="min-w-[240px]"
                            >
                                部門名
                            </CommonTableHeaderCell>
                            <CommonTableHeaderCell
                                sortable
                                sortActive={shortNameFilter.open}
                                sortDirection={sortFor("shortName")}
                                filterCount={shortNameFilter.filterCount}
                                onSort={(anchor) => openOnly("shortName", anchor)}
                                className="min-w-[160px]"
                            >
                                略称
                            </CommonTableHeaderCell>
                        </tr>
                    </thead>
                    <tbody>
                        {loading ? (
                            <CommonTableRow>
                                <CommonTableCell colSpan={DEPARTMENT_COLUMN_COUNT} className="py-8 text-gray-500">読み込み中...</CommonTableCell>
                            </CommonTableRow>
                        ) : displayedDepartments.length === 0 ? (
                            <CommonTableRow>
                                <CommonTableCell colSpan={DEPARTMENT_COLUMN_COUNT} className="py-8 text-gray-500">
                                    {departments.length === 0 ? "部門が登録されていません" : "該当する部門がありません"}
                                </CommonTableCell>
                            </CommonTableRow>
                        ) : (
                            displayedDepartments.map((dept) => (
                                <CommonTableRow key={dept.id}>
                                    <CommonTableEditCell onClick={() => navigate(`/org-settings/department/${dept.id}/edit`)} />
                                    <CommonTableCell>{displayValue(dept.code)}</CommonTableCell>
                                    <CommonTableCell>{displayValue(dept.name)}</CommonTableCell>
                                    <CommonTableCell>{displayValue(getShortName(dept))}</CommonTableCell>
                                </CommonTableRow>
                            ))
                        )}
                    </tbody>
                </CommonTable>
            </div>

            {/* 列ごとの並び替え＋絞り込みポップアップ */}
            <ColumnFilterPopup
                isOpen={codeFilter.open}
                onClose={() => codeFilter.setOpen(false)}
                anchorRef={codeFilter.anchorRef}
                sortDirection={sortFor("code")}
                onSortChange={handleSortChange("code")}
                options={codeFilter.options}
                selectedValues={codeFilter.selected}
                onSelectedValuesChange={codeFilter.setSelected}
            />
            <ColumnFilterPopup
                isOpen={nameFilter.open}
                onClose={() => nameFilter.setOpen(false)}
                anchorRef={nameFilter.anchorRef}
                sortDirection={sortFor("name")}
                onSortChange={handleSortChange("name")}
                options={nameFilter.options}
                selectedValues={nameFilter.selected}
                onSelectedValuesChange={nameFilter.setSelected}
            />
            <ColumnFilterPopup
                isOpen={shortNameFilter.open}
                onClose={() => shortNameFilter.setOpen(false)}
                anchorRef={shortNameFilter.anchorRef}
                sortDirection={sortFor("shortName")}
                onSortChange={handleSortChange("shortName")}
                options={shortNameFilter.options}
                selectedValues={shortNameFilter.selected}
                onSelectedValuesChange={shortNameFilter.setSelected}
            />
        </div>
    );
};

export default OrgSettingsDepartmentPage;
